import { useEffect, useMemo, useState } from 'react'
import {
  AlarmClock, ArrowUpRight, BriefcaseBusiness, Building2, Check, ChevronRight, Clock3, Coffee,
  Footprints, Home, Laptop, Moon, MoreHorizontal, Pencil, Plane, RotateCcw,
  Sandwich, Sun, Utensils, X,
} from 'lucide-react'
import type { ActionOption, BootstrapDto, EventDto, MetricsDto, UserAction } from '../types'
import { api } from '../lib/api'
import { clock, dateTime, duration, fromDateTimeLocal, toDateTimeLocal } from '../lib/format'
import { useLiveMetrics } from '../hooks/useLiveMetrics'
import { useDialogAccessibility } from '../hooks/useDialogAccessibility'
import '../dashboard.css'

interface Props {
  data: BootstrapDto
  busy: boolean
  onAction: (action: UserAction) => Promise<void>
  onData: (data: BootstrapDto) => void
  onError: (message?: string) => void
  onToast: (message: string) => void
}

const stateCopy = {
  SLEEPING: { eyebrow: 'Отдых', title: 'Вы спите', icon: Moon, accent: 'violet' },
  HOME: { eyebrow: 'Дома', title: 'Время для себя', icon: Home, accent: 'amber' },
  COMMUTING: { eyebrow: 'В пути', title: 'Дорога на работу', icon: Footprints, accent: 'blue' },
  WORKING: { eyebrow: 'Работа', title: 'Сессия идёт', icon: BriefcaseBusiness, accent: 'green' },
  BREAKFAST: { eyebrow: 'Перерыв', title: 'Завтрак', icon: Sandwich, accent: 'amber' },
  LUNCH: { eyebrow: 'Перерыв', title: 'Обед', icon: Utensils, accent: 'amber' },
  DINNER: { eyebrow: 'Перерыв', title: 'Ужин', icon: Utensils, accent: 'amber' },
  DISTRACTED: { eyebrow: 'Пауза', title: 'Вы отвлеклись', icon: Coffee, accent: 'blue' },
  SMOKING: { eyebrow: 'Пауза', title: 'Перекур', icon: MoreHorizontal, accent: 'blue' },
  OUTSIDE_OFFICE: { eyebrow: 'Работа завершена', title: 'Можно отдыхать', icon: Check, accent: 'green' },
  COMMUTING_HOME: { eyebrow: 'В пути', title: 'Дорога домой', icon: Footprints, accent: 'blue' },
} as const

const actionIcons: Partial<Record<UserAction, typeof Clock3>> = {
  WAKE_UP: Sun, LEAVE_HOME: Footprints, LEAVE_HOME_UNTRACKED: Footprints,
  START_OFFICE: Building2, START_REMOTE: Laptop, START_BUSINESS_TRIP: Plane,
  START_BREAKFAST: Sandwich, END_BREAKFAST: BriefcaseBusiness,
  START_LUNCH: Utensils, END_LUNCH: BriefcaseBusiness,
  START_DINNER: Utensils, END_DINNER: BriefcaseBusiness,
  START_DISTRACTION: Coffee, END_DISTRACTION: BriefcaseBusiness,
  START_SMOKING: MoreHorizontal, END_SMOKING: BriefcaseBusiness,
  END_WORK: Check, GO_TO_SLEEP: Moon,
  START_COMMUTE_HOME: Footprints, START_COMMUTE_HOME_UNTRACKED: Footprints,
  ARRIVE_HOME: Home, ARRIVE_HOME_UNTRACKED: Home,
}

const primaryActionHelp: Partial<Record<UserAction, string>> = {
  WAKE_UP: 'Начать новый цикл дня',
  LEAVE_HOME: 'Начать отсчёт дороги на работу',
  START_OFFICE: 'Начать рабочую сессию в офисе',
  START_REMOTE: 'Начать рабочую сессию из дома',
  START_BUSINESS_TRIP: 'Начать сессию в командировке',
  END_WORK: 'Завершить рабочую сессию',
  GO_TO_SLEEP: 'Завершить день и начать отсчёт сна',
  START_COMMUTE_HOME: 'Начать отсчёт обратной дороги',
  ARRIVE_HOME: 'Сохранить время дороги домой',
  ARRIVE_HOME_UNTRACKED: 'Завершить поездку и исключить её из статистики',
}

export default function DashboardPage({ data, busy, onAction, onData, onError, onToast }: Props) {
  const metrics = useLiveMetrics(data)!
  const hasTarget = metrics.targetSeconds > 0
  const state = stateCopy[data.user.currentState]
  const StateIcon = state.icon
  const [editing, setEditing] = useState<EventDto>()
  const [editingTarget, setEditingTarget] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [pendingAction, setPendingAction] = useState<UserAction>()
  const [now, setNow] = useState(Date.now())
  const latest = data.recentEvents[0]
  const stateDuration = latest ? Math.max(0, Math.floor((now - new Date(latest.occurredAt).getTime()) / 1000)) : undefined
  const targetReached = hasTarget && metrics.netSeconds >= metrics.targetSeconds
  const progressPercent = hasTarget ? Math.min(100, Math.max(0, metrics.netSeconds / metrics.targetSeconds * 100)) : 0
  const [primaryAction, ...otherActions] = data.availableActions
  const actionBusy = busy || pendingAction !== undefined

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const minute = Math.floor(now / 60_000)
  const dateContext = useMemo(() => {
    const instant = new Date(minute * 60_000)
    const hour = Number(new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', hourCycle: 'h23', timeZone: data.user.timeZone,
    }).format(instant))
    return {
      greeting: hour < 6 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер',
      date: new Intl.DateTimeFormat('ru-RU', {
        weekday: 'long', day: 'numeric', month: 'long', timeZone: data.user.timeZone,
      }).format(instant),
    }
  }, [data.user.timeZone, minute])

  const act = async (action: UserAction) => {
    if (actionBusy) return
    setPendingAction(action)
    try { await onAction(action) }
    finally { setPendingAction(undefined) }
  }

  const undo = async () => {
    if (undoing || busy || !window.confirm('Отменить последнее действие? Запись останется в аудите.')) return
    setUndoing(true)
    try {
      onData(await api.undo('Ошибочное последнее действие'))
      onToast('Последнее действие отменено')
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Не удалось отменить действие')
    } finally { setUndoing(false) }
  }

  return (
    <div className="page dashboard-page">
      <header className="top-header dashboard-header">
        <div>
          <span className="dashboard-greeting">{dateContext.greeting}{data.user.firstName ? `, ${data.user.firstName}` : ''}</span>
          <h1>Ваш день</h1>
          <p className="dashboard-date">{dateContext.date}</p>
        </div>
        <div className="avatar" aria-hidden="true">{(data.user.firstName || data.user.username || 'U').slice(0, 1).toUpperCase()}</div>
      </header>

      <div className="dashboard-workspace">
        <section className={`status-hero dashboard-hero ${state.accent}`} aria-label="Текущее состояние и рабочая цель">
          <div className="status-glow" />
          <div className="dashboard-state">
            <span className="dashboard-state-icon"><StateIcon size={23} aria-hidden="true" /></span>
            <div className="dashboard-state-copy"><span>{state.eyebrow}</span><h2>{state.title}</h2></div>
            {stateDuration !== undefined && <div className="dashboard-state-duration"><span>В этом состоянии</span><b>{duration(stateDuration, true)}</b></div>}
          </div>

          <div className={`dashboard-goal-visual ${targetReached ? 'complete' : ''} ${hasTarget ? '' : 'no-target'}`}>
            <div className="dashboard-goal-ring">
              <svg viewBox="0 0 200 200" role={hasTarget ? 'progressbar' : undefined} aria-hidden={hasTarget ? undefined : true} aria-label={hasTarget ? 'Выполнение рабочей цели' : undefined} aria-valuenow={hasTarget ? Math.round(progressPercent) : undefined} aria-valuemin={hasTarget ? 0 : undefined} aria-valuemax={hasTarget ? 100 : undefined} aria-valuetext={hasTarget ? `${duration(metrics.netSeconds, true)} из ${duration(metrics.targetSeconds, true)}` : undefined}>
                <circle className="dashboard-ring-track" cx="100" cy="100" r="89" pathLength="100" />
                {hasTarget && <circle className="dashboard-ring-fill" cx="100" cy="100" r="89" pathLength="100" strokeDasharray="100" strokeDashoffset={100 - progressPercent} />}
              </svg>
              <div className="dashboard-ring-center"><span>Чистое время</span><strong>{duration(metrics.netSeconds)}</strong><small>{hasTarget ? `из ${duration(metrics.targetSeconds)}` : 'без цели'}</small></div>
            </div>
            <div className="dashboard-goal-copy">
              <span className="dashboard-goal-eyebrow">{!hasTarget ? 'Свободный ритм' : targetReached ? <><Check size={17} aria-hidden="true" />Цель выполнена</> : 'До цели осталось'}</span>
              <strong className={targetReached ? 'positive' : ''}>{!hasTarget ? 'Без цели' : targetReached ? metrics.overtimeSeconds >= 60 ? `+${duration(metrics.overtimeSeconds, true)}` : 'По плану' : duration(metrics.remainingSeconds, true)}</strong>
              <p>{!hasTarget ? 'Работа и отдых в вашем темпе' : targetReached ? metrics.overtimeSeconds >= 60 ? 'сверх рабочего плана' : 'Рабочий план на сегодня выполнен' : `${Math.floor(progressPercent)}% рабочего плана выполнено`}</p>
              <button onClick={() => setEditingTarget(true)} className="dashboard-goal-edit" aria-label="Изменить цель на сегодня"><Pencil size={15} aria-hidden="true" /><span>{hasTarget ? 'Изменить цель' : 'Задать цель'}</span></button>
            </div>
          </div>
          <p className="dashboard-session-context">{data.activeSession
            ? <><BriefcaseBusiness size={16} aria-hidden="true" />{data.activeSession.modeLabel} · с {clock(data.activeSession.startedAt, data.user.timeZone)}</>
            : metrics.grossSeconds > 0 ? 'За текущий рабочий цикл' : 'Отметьте начало работы — время пойдёт автоматически'}</p>

          {data.activeSession && hasTarget && metrics.remainingSeconds > 0 && metrics.projectedExitAt && <div className="dashboard-projection">
            <AlarmClock size={21} aria-hidden="true" />
            <div><span>Цель к <b>{clock(metrics.projectedExitAt, data.user.timeZone)}</b></span><p>{data.user.currentState === 'WORKING' ? 'Если продолжать без новых пауз' : 'Если вернуться к работе сейчас и продолжать без пауз'}</p></div>
            <ArrowUpRight size={19} aria-hidden="true" />
          </div>}
          {(data.activeSession || metrics.grossSeconds > 0) && <DashboardMetrics metrics={metrics} className="dashboard-metrics-desktop" />}
        </section>

        <section className="dashboard-actions" aria-labelledby="dashboard-actions-title">
          <div className="dashboard-section-heading"><span className="eyebrow">Быстрая отметка</span><h2 id="dashboard-actions-title">Что дальше?</h2></div>
          {primaryAction && <DashboardAction option={primaryAction} featured disabled={actionBusy} pending={pendingAction === primaryAction.action} onAction={act} />}
          {otherActions.length > 0 && <div className="dashboard-secondary-actions" aria-label="Другие доступные отметки">
            {otherActions.map((option) => <DashboardAction key={option.action} option={option} disabled={actionBusy} pending={pendingAction === option.action} onAction={act} />)}
          </div>}
          {data.availableActions.length === 0 && <p className="dashboard-action-note">Доступные действия появятся после обновления состояния.</p>}
          <p className="dashboard-action-note"><Clock3 size={15} aria-hidden="true" />Время отметки можно исправить в хронологии.</p>
        </section>
        {(data.activeSession || metrics.grossSeconds > 0) && <DashboardMetrics metrics={metrics} className="dashboard-metrics-mobile" />}
      </div>

      <section className="timeline-card dashboard-timeline" aria-labelledby="dashboard-timeline-title">
        <div className="section-title-row">
          <div><span className="eyebrow">Последние отметки</span><h2 id="dashboard-timeline-title">Хронология</h2></div>
          {data.canUndo && <button className="icon-text-button" disabled={undoing || busy} onClick={undo}><RotateCcw size={17} aria-hidden="true" />{undoing ? 'Отменяю…' : 'Отменить последнее'}</button>}
        </div>
        {data.recentEvents.length === 0 ? (
          <div className="empty-state small"><Clock3 size={28} aria-hidden="true" /><p>Здесь появятся ваши отметки.<br />Начните день с действия выше.</p></div>
        ) : (
          <ol className="timeline-list dashboard-timeline-list">
            {data.recentEvents.slice(0, 6).map((event, index) => (
              <li className="timeline-item" key={event.id}>
                <div className={`timeline-dot ${index === 0 ? 'current' : ''}`} aria-hidden="true" />
                <div className="timeline-main"><b>{event.label}</b><small><time dateTime={event.occurredAt}>{dateTime(event.occurredAt, data.user.timeZone)}</time><span className="dashboard-event-source">{sourceLabel(event.source)}</span></small></div>
                <button className="edit-event" aria-label={`Исправить время: ${event.label}`} title="Исправить время" disabled={busy} onClick={() => setEditing(event)}><Pencil size={17} aria-hidden="true" /></button>
              </li>
            ))}
          </ol>
        )}
      </section>

      {editing && <EditEventModal event={editing} timeZone={data.user.timeZone} onClose={() => setEditing(undefined)} onSave={async (occurredAt, reason) => {
        onData(await api.correctEvent(editing.id, occurredAt, reason))
        setEditing(undefined)
        onToast('Время исправлено, аудит сохранён')
      }} />}
      {editingTarget && <TodayTargetModal
        targetMinutes={data.todayPlan?.effectiveTargetMinutes ?? Math.round(metrics.targetSeconds / 60)}
        overridden={data.todayPlan?.overrideTargetMinutes != null}
        onClose={() => setEditingTarget(false)}
        onSave={async (target) => {
          onData(await api.updateTodayTarget(target))
          setEditingTarget(false)
          onToast('Цель на сегодня изменена')
        }}
        onReset={async () => {
          onData(await api.resetTodayTarget())
          setEditingTarget(false)
          onToast('Цель возвращена к расписанию')
        }}
      />}
    </div>
  )
}

function DashboardAction({ option, featured = false, disabled, pending, onAction }: {
  option: ActionOption; featured?: boolean; disabled: boolean; pending: boolean; onAction: (action: UserAction) => Promise<void>
}) {
  const Icon = actionIcons[option.action] || Clock3
  return <button className={`dashboard-action ${option.tone} ${featured ? 'featured' : ''}`} disabled={disabled} onClick={() => void onAction(option.action)}>
    <span className="dashboard-action-icon"><Icon size={featured ? 24 : 21} aria-hidden="true" /></span>
    <span className="dashboard-action-label"><b>{pending ? 'Сохраняю…' : option.label}</b>{featured && primaryActionHelp[option.action] && <small>{primaryActionHelp[option.action]}</small>}</span>
    <ChevronRight size={19} className="dashboard-action-arrow" aria-hidden="true" />
  </button>
}

function Metric({ icon: Icon, label, value, tone }: { icon: typeof Clock3; label: string; value: string; tone: string }) {
  return <div className={`dashboard-metric-tile ${tone}`}><dt><span className="dashboard-metric-tile-icon"><Icon size={18} aria-hidden="true" /></span>{label}</dt><dd>{value}</dd></div>
}

function DashboardMetrics({ metrics, className }: { metrics: MetricsDto; className: string }) {
  return <dl className={`dashboard-metrics ${className}`} aria-label="Подробности рабочего времени">
    <Metric icon={Clock3} label="На работе" value={duration(metrics.grossSeconds)} tone="violet" />
    <Metric icon={Coffee} label="Исключённые паузы" value={duration(metrics.excludedBreakSeconds)} tone="amber" />
    <Metric icon={MoreHorizontal} label="Отвлечения" value={duration(metrics.distractionSeconds || 0)} tone="blue" />
  </dl>
}

function EditEventModal({ event, timeZone, onClose, onSave }: {
  event: EventDto; timeZone: string; onClose: () => void; onSave: (occurredAt: string, reason: string) => Promise<void>
}) {
  const [time, setTime] = useState(toDateTimeLocal(event.occurredAt, timeZone))
  const [reason, setReason] = useState('Исправление ошибочной отметки')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const dialogRef = useDialogAccessibility(() => { if (!saving) onClose() })
  return <div className="modal-backdrop dashboard-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
    <div className="modal-card dashboard-dialog" role="dialog" aria-modal="true" aria-labelledby="edit-event-title" aria-describedby="edit-event-description" ref={dialogRef} tabIndex={-1}>
      <button className="dashboard-dialog-close" aria-label="Закрыть" disabled={saving} onClick={onClose}><X size={21} /></button>
      <span className="eyebrow">Исправление события</span><h2 id="edit-event-title">{event.label}</h2><p id="edit-event-description">Исходное и новое время останутся в журнале аудита.</p>
      <form onSubmit={async (event) => {
        event.preventDefault()
        if (saving || !reason.trim()) return
        if (!time) { setError('Укажите дату и время события'); return }
        setSaving(true); setError(undefined)
        try { await onSave(fromDateTimeLocal(time, timeZone), reason.trim()) }
        catch (cause) { setError(cause instanceof Error ? cause.message : 'Не удалось исправить время') }
        finally { setSaving(false) }
      }}>
        <label className="field-label" htmlFor="event-date-time">Дата и время</label><input id="event-date-time" data-dialog-autofocus className="text-input" type="datetime-local" required disabled={saving} value={time} onChange={(event) => setTime(event.target.value)} />
        <label className="field-label" htmlFor="event-reason">Причина исправления</label><textarea id="event-reason" className="text-input" required disabled={saving} value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} />
        {error && <div className="modal-error" role="alert">{error}</div>}
        <div className="modal-actions"><button className="button ghost" type="button" disabled={saving} onClick={onClose}>Отмена</button><button className="button primary" type="submit" disabled={!reason.trim() || saving}>{saving ? 'Сохраняю…' : 'Сохранить'}</button></div>
      </form>
    </div>
  </div>
}

function sourceLabel(source: EventDto['source']) {
  return { BOT: 'Бот', MINI_APP: 'Mini App', WEB: 'Веб', ADMIN: 'Администратор', IMPORT: 'Импорт' }[source]
}

function TodayTargetModal({ targetMinutes, overridden, onClose, onSave, onReset }: {
  targetMinutes: number; overridden: boolean; onClose: () => void
  onSave: (target: number) => Promise<void>; onReset: () => Promise<void>
}) {
  const [hours, setHours] = useState(String(Math.floor(targetMinutes / 60)))
  const [minutes, setMinutes] = useState(String(targetMinutes % 60))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const dialogRef = useDialogAccessibility(() => { if (!saving) onClose() })
  const target = Number(hours) * 60 + Number(minutes)
  const valid = hours.trim() !== '' && minutes.trim() !== '' && Number.isInteger(Number(hours)) && Number.isInteger(Number(minutes)) && Number(hours) >= 0 && Number(minutes) >= 0 && Number(minutes) < 60 && target <= 1440
  const setTarget = (value: number) => { setHours(String(Math.floor(value / 60))); setMinutes(String(value % 60)) }
  const run = async (operation: () => Promise<void>) => {
    if (saving) return
    setSaving(true); setError(undefined)
    try { await operation() }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Не удалось изменить цель') }
    finally { setSaving(false) }
  }
  return <div className="modal-backdrop dashboard-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
    <div className="modal-card dashboard-dialog" role="dialog" aria-modal="true" aria-labelledby="today-target-title" aria-describedby="today-target-description" ref={dialogRef} tabIndex={-1}>
      <button className="dashboard-dialog-close" aria-label="Закрыть" disabled={saving} onClick={onClose}><X size={21} /></button>
      <span className="eyebrow">План дня</span><h2 id="today-target-title">Цель на сегодня</h2><p id="today-target-description">Для всего текущего рабочего цикла. Следующий день начнётся с нормы по расписанию.</p>
      <form onSubmit={(event) => { event.preventDefault(); if (valid) void run(() => onSave(target)) }}>
        <div className="dashboard-target-inputs">
          <div><label className="field-label" htmlFor="target-hours">Часы</label><input id="target-hours" data-dialog-autofocus className="text-input" type="number" inputMode="numeric" min={0} max={24} step={1} required disabled={saving} value={hours} onChange={(event) => setHours(event.target.value)} /></div>
          <div><label className="field-label" htmlFor="target-minutes">Минуты</label><input id="target-minutes" className="text-input" type="number" inputMode="numeric" min={0} max={59} step={1} required disabled={saving} value={minutes} onChange={(event) => setMinutes(event.target.value)} /></div>
        </div>
        <div className="target-quick" aria-label="Быстрый выбор цели">{[0, 420, 480, 540].map((value) => <button type="button" key={value} disabled={saving} aria-pressed={valid && target === value} className={valid && target === value ? 'active' : ''} onClick={() => setTarget(value)}>{value === 0 ? 'Без цели' : `${value / 60} ч`}</button>)}</div>
        <p className="dashboard-target-preview">{valid ? target === 0 ? 'Свободный день — без рабочей цели' : `План: ${duration(target * 60, true)} чистой работы` : 'Укажите время от 0 до 24 часов'}</p>
        {error && <div className="modal-error" role="alert">{error}</div>}
        {overridden && <button className="reset-target" type="button" disabled={saving} onClick={() => void run(onReset)}>Вернуть норму по расписанию</button>}
        <div className="modal-actions"><button className="button ghost" type="button" disabled={saving} onClick={onClose}>Отмена</button><button className="button primary" type="submit" disabled={saving || !valid}>{saving ? 'Сохраняю…' : 'Сохранить цель'}</button></div>
      </form>
    </div>
  </div>
}
